import React from 'react';

// === Components === //
import Container from '@components/common/Container';

// === Helpers === //
import * as utils from '@utils';

// === Styles === //
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { breakpoints, colors, respondFrom } from '@styles';

// === Types === //
import { Variant } from '@content/types/layout';

const SpeechBallonsWrapper = styled.div`
  position: relative;
  margin-top: 30px;
  margin-bottom: 50px;

  ${respondFrom(
    breakpoints.lg,
    css`
      margin-top: 60px;
      margin-bottom: 100px;
    `
  )};
`;

const SpeechBallonsRow = styled.div`
  display: flex;
  flex-flow: column;
  align-items: center;

  &:nth-of-type(1) {
    margin-bottom: 220px;
  }

  ${respondFrom(
    breakpoints.md,
    css`
      justify-content: space-between;
      flex-flow: row;

      &:nth-of-type(1) {
        margin-bottom: 120px;
      }
    `
  )};

  ${respondFrom(
    breakpoints.lg,
    css`
      &:nth-of-type(1) {
        margin-bottom: 0;
      }
    `
  )};
`;

const SpeechBallon = styled.div<SppechBallonProps>`
  background-color: ${(props) =>
    props.variant === 'blue' ? colors.ui.patientDefault : colors.ui.hcpDefault};
  color: ${colors.white};
  display: flex;
  align-items: center;
  position: relative;
  width: 100%;

  p {
    margin: 0;
  }

  &::after {
    content: '';
    width: 0;
    height: 0;
    border-style: solid;
    _filter: progid:DXImageTransform.Microsoft.Chroma(color='#000000');
    line-height: 0px;
    position: absolute;
  }

  &.speechBallon {
    &--top {
      margin-bottom: 15px;

      ${respondFrom(
        breakpoints.lg,
        css`
          margin-bottom: 45px;
        `
      )};

      &::after {
        bottom: -45px;
      }
    }

    &--bottom {
      margin-top: 15px;

      ${respondFrom(
        breakpoints.lg,
        css`
          margin-top: 45px;
        `
      )};

      &::after {
        top: -45px;
      }
    }

    &--rounded {
      border-radius: 99px;
      padding: 48px 55px;
    }

    &--rectangular {
      border-radius: 10px;
      padding: 47px 20px 47px 55px;
    }

    &--1 {
      ${respondFrom(
        breakpoints.md,
        css`
          max-width: 415px;
          margin-right: 20px;
        `
      )};

      ${respondFrom(
        breakpoints.lg,
        css`
          margin-left: 40px;
          margin-right: 0;
        `
      )};

      &::after {
        transform: skew(20deg, 0deg) rotate(0deg);
        border-width: 0 45px 48px 0;
        border-color: transparent
          ${(props) => (props.variant === 'blue' ? colors.ui.patientDefault : colors.ui.hcpDefault)}
          transparent transparent;
        _border-color: #000000
          ${(props) => (props.variant === 'blue' ? colors.ui.patientDefault : colors.ui.hcpDefault)}
          #000000 #000000;
        right: 65px;
      }
    }

    &--2 {
      ${respondFrom(
        breakpoints.md,
        css`
          max-width: 353px;
        `
      )};

      &::after {
        transform: skew(-20deg, 0deg) rotate(0deg);
        border-width: 45px 45px 0 0;
        border-color: ${(props) =>
            props.variant === 'blue' ? colors.ui.patientDefault : colors.ui.hcpDefault}
          transparent transparent transparent;
        _border-color: ${(props) =>
            props.variant === 'blue' ? colors.ui.patientDefault : colors.ui.hcpDefault}
          #000000 #000000 #000000;
        right: 60px;
        bottom: -35px;

        ${respondFrom(
          breakpoints.md,
          css`
            right: 0;
            left: 80px;
          `
        )};
      }
    }

    &--3 {
      ${respondFrom(
        breakpoints.md,
        css`
          max-width: 385px;
        `
      )};

      &::after {
        transform: skew(7deg, 13deg) rotate(-15deg);
        border-width: 48px 0 0 45px;
        border-color: transparent transparent transparent
          ${(props) => (props.variant === 'blue' ? colors.ui.patientDefault : colors.ui.hcpDefault)};
        line-height: 0px;
        _border-color: #000000 #000000 #000000
          ${(props) => (props.variant === 'blue' ? colors.ui.patientDefault : colors.ui.hcpDefault)};
        right: 65px;

        ${(props) =>
          respondFrom(
            breakpoints.md,
            css`
              transform: skew(-7deg, -13deg) rotate(14deg);
              border-width: 0 0 45px 45px;
              border-color: transparent transparent
                ${props.variant === 'blue' ? colors.ui.patientDefault : colors.ui.hcpDefault}
                transparent;
              _border-color: #000000 #000000
                ${props.variant === 'blue' ? colors.ui.patientDefault : colors.ui.hcpDefault}
                #000000;
            `
          )}
      }
    }

    &--4 {
      ${respondFrom(
        breakpoints.md,
        css`
          max-width: 328px;
        `
      )};

      &::after {
        transform: skew(-7deg, -13deg) rotate(14deg);
        border-width: 0 0 45px 45px;
        border-color: transparent transparent
          ${(props) => (props.variant === 'blue' ? colors.ui.patientDefault : colors.ui.hcpDefault)}
          transparent;
        _border-color: #000000 #000000
          ${(props) => (props.variant === 'blue' ? colors.ui.patientDefault : colors.ui.hcpDefault)}
          #000000;
        right: 35px;
        top: -35px;

        ${(props) =>
          respondFrom(
            breakpoints.md,
            css`
              transform: skew(7deg, 13deg) rotate(-15deg);
              border-width: 48px 0 0 45px;
              border-color: transparent transparent transparent
                ${props.variant === 'blue' ? colors.ui.patientDefault : colors.ui.hcpDefault};
              line-height: 0px;
              _border-color: #000000 #000000 #000000
                ${props.variant === 'blue' ? colors.ui.patientDefault : colors.ui.hcpDefault};
              right: 0;
              left: 55px;
            `
          )}
      }
    }
  }
`;

const SppechImage = styled.img`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  margin: auto;
  width: 180px;
  z-index: -1;

  ${respondFrom(
    breakpoints.md,
    css`
      width: 270px;
      top: 50px;
      left: 100px;
    `
  )};
`;

interface SpeechBalloonsProps {
  variant: Variant;
  image: string | null;
  imageAlt: string;
  imageTitle: string;
  text: SpeechBallonsTextData;
}

interface SpeechBallonsTextData {
  t1: string;
  t2: string;
  t3: string;
  t4: string;
}

interface SppechBallonProps {
  variant: Variant;
}

const SpeechBalloons = ({ variant, image, imageAlt, imageTitle, text }: SpeechBalloonsProps) => {
  return (
    <Container>
      <SpeechBallonsWrapper>
        <SpeechBallonsRow>
          <SpeechBallon
            variant={variant}
            className="speechBallon speechBallon--top speechBallon--rounded speechBallon--1"
          >
            {utils.SafeHtml(text.t1)}
          </SpeechBallon>
          <SpeechBallon
            variant={variant}
            className="speechBallon speechBallon--top speechBallon--rectangular speechBallon--2"
          >
            {utils.SafeHtml(text.t2)}
          </SpeechBallon>
        </SpeechBallonsRow>

        {image && image !== '' && <SppechImage src={image} alt={imageAlt} title={imageTitle} />}

        <SpeechBallonsRow>
          <SpeechBallon
            variant={variant}
            className="speechBallon speechBallon--bottom speechBallon--rectangular speechBallon--3"
          >
            {utils.SafeHtml(text.t3)}
          </SpeechBallon>
          <SpeechBallon
            variant={variant}
            className="speechBallon speechBallon--bottom speechBallon--rounded speechBallon--4"
          >
            {utils.SafeHtml(text.t4)}
          </SpeechBallon>
        </SpeechBallonsRow>
      </SpeechBallonsWrapper>
    </Container>
  );
};

export default SpeechBalloons;
